import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';

const OnboardingTemplates = () => {
  const { success, error: showError } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ taskName: '', taskType: 'manual' });

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({ taskName: '', taskType: 'manual' });

  const loadTemplates = () => {
    setLoading(true);
    apiClient.get('/onboarding/templates')
      .then(({ data }) => setTemplates(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load templates')))
      .finally(() => setLoading(false));
  };

  useEffect(loadTemplates, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTasks = (templateId) => {
    setSelectedTemplateId(templateId);
    setEditingTaskId(null);
    if (!templateId) { setTasks([]); return; }
    apiClient.get(`/onboarding/templates/${templateId}/tasks`)
      .then(({ data }) => setTasks(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load tasks')));
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/onboarding/templates', { name: templateName });
      success('✅ Template created');
      setShowAdd(false);
      setTemplateName('');
      loadTemplates();
    } catch (err) { showError(getErrorMessage(err, 'Could not create template')); }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/onboarding/templates/${selectedTemplateId}/tasks`, taskForm);
      success('✅ Task added');
      setShowAddTask(false);
      setTaskForm({ taskName: '', taskType: 'manual' });
      loadTasks(selectedTemplateId);
    } catch (err) { showError(getErrorMessage(err, 'Could not add task')); }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditTaskForm({ taskName: task.taskName, taskType: task.taskType || 'manual' });
  };

  const handleUpdateTask = async (e, taskId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/onboarding/templates/${selectedTemplateId}/tasks/${taskId}`, editTaskForm);
      success('✅ Task updated');
      setEditingTaskId(null);
      loadTasks(selectedTemplateId);
    } catch (err) { showError(getErrorMessage(err, 'Could not update task')); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await apiClient.delete(`/onboarding/templates/${selectedTemplateId}/tasks/${taskId}`);
      success('✅ Task deleted');
      loadTasks(selectedTemplateId);
    } catch (err) { showError(getErrorMessage(err, 'Could not delete task')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Onboarding Templates</h1>
        <p>Reusable checklists for starting a new employee. To start onboarding for a specific person, go to Employees.</p>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2>Templates</h2>
          <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAdd((s) => !s)}>
            <FiPlus size={14} /> {showAdd ? 'Cancel' : 'New Template'}
          </button>
        </div>
        {showAdd && (
          <form onSubmit={handleAddTemplate} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
              <label>Template Name *</label>
              <input required value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Standard New Starter" />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button>
          </form>
        )}
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : templates.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No templates yet</td></tr>
              ) : templates.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>{t.isActive ? 'Yes' : 'No'}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => loadTasks(t.id)}>
                      Manage Tasks
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedTemplateId && (
        <section className="page-section">
          <div className="section-header">
            <h2>Tasks — {templates.find(t => t.id === selectedTemplateId)?.name}</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddTask((s) => !s)}>
              <FiPlus size={14} /> {showAddTask ? 'Cancel' : 'Add Task'}
            </button>
          </div>
          {showAddTask && (
            <form onSubmit={handleAddTask} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Task Name *</label>
                <input required value={taskForm.taskName} onChange={(e) => setTaskForm(p => ({ ...p, taskName: e.target.value }))} placeholder="Sign employment contract" />
              </div>
              <div className="form-field">
                <label>Task Type</label>
                <select value={taskForm.taskType} onChange={(e) => setTaskForm(p => ({ ...p, taskType: e.target.value }))}>
                  <option value="manual">Manual</option>
                  <option value="document_upload">Document Upload</option>
                  <option value="esignature">E-signature</option>
                  <option value="system">System</option>
                </select>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add Task</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Task</th><th>Type</th><th>Actions</th></tr></thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No tasks yet</td></tr>
                ) : tasks.map((t) => (
                  <tr key={t.id}>
                    {editingTaskId === t.id ? (
                      <td colSpan={3}>
                        <form onSubmit={(e) => handleUpdateTask(e, t.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0' }}>
                          <input required value={editTaskForm.taskName} onChange={(e) => setEditTaskForm(p => ({ ...p, taskName: e.target.value }))} style={{ flex: 1, padding: '0.4rem' }} />
                          <select value={editTaskForm.taskType} onChange={(e) => setEditTaskForm(p => ({ ...p, taskType: e.target.value }))} style={{ padding: '0.4rem' }}>
                            <option value="manual">Manual</option>
                            <option value="document_upload">Document Upload</option>
                            <option value="esignature">E-signature</option>
                            <option value="system">System</option>
                          </select>
                          <button type="submit" className="action-icon edit" title="Save"><FiSave size={16} /></button>
                          <button type="button" className="action-icon delete" title="Cancel" onClick={() => setEditingTaskId(null)}><FiX size={16} /></button>
                        </form>
                      </td>
                    ) : (
                      <>
                        <td style={{ fontWeight: 600 }}>{t.taskName}</td>
                        <td style={{ textTransform: 'capitalize' }}>{t.taskType?.replace('_', ' ')}</td>
                        <td>
                          <div className="actions">
                            <button className="action-icon edit" title="Edit" onClick={() => startEditTask(t)}>
                              <FiEdit2 size={16} />
                            </button>
                            <button className="action-icon delete" title="Delete" onClick={() => handleDeleteTask(t.id)}>
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default OnboardingTemplates;